import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import { FieldRenderProps } from 'react-final-form'
import { makeStyles } from 'tss-react/mui'
import styles from '../../styles'
import { Grid, outlinedInputClasses, styled, MenuItem, InputLabel, FormControl, OutlinedInput } from '@mui/material'
import { fontFamilyMonserrat } from '../../theme'
import { SelectOption } from '../../types/fields/fieldsModel'
import Select from '@mui/material/Select'
import SvgSelectAdornment from '../../icons/SvgSelectAdorment'
import { ErrorContainer } from './utils/styledComponents'

interface AcceptingProps {
	label: string
	placeholder?: string
	options: SelectOption[]
}

type SelectFieldProps = AcceptingProps & FieldRenderProps<string[]>

const SelectField: FC<SelectFieldProps> = (
	{
		label,
		input: { name, ...restInput },
		meta: { touched, error },
		options
	}) => {
	const intl = useIntl()
	const { classes } = useStyles()

	return (
		<CustomFormControl variant="outlined">
			<SelectFieldContainer container item>
				<InputLabel className={classes.labelStyle} shrink>{intl.formatMessage({ id: label })}</InputLabel>
				<CustomSelectField
					{...restInput}
					label={intl.formatMessage({ id: label })}
					input={<OutlinedInput notched label={intl.formatMessage({ id: label })} />}
					IconComponent={SvgSelectAdornment}
				>
					{options.map((option, key) => (<CustomMenuItem key={`option-${name}-${key}`} value={option.value}>{option.label}</CustomMenuItem>))}
				</CustomSelectField>
				{touched && error && <Grid item><ErrorContainer>{error}</ErrorContainer></Grid>}
			</SelectFieldContainer>
		</CustomFormControl>
	)
}

const useStyles = makeStyles()((theme) => ({
	...styles(theme),
	labelStyle: {
		color: theme.palette.blue['50'],
		fontFamily: fontFamilyMonserrat,
		'&.Mui-focused': {
			color: theme.palette.blue['50']
		},
		paddingLeft: 8
	}
}))

const CustomSelectField = styled(Select)(({ theme }) => ({
	[`& .${outlinedInputClasses.input}`]: {
		...theme.typography.text8
	},
	[`& .${outlinedInputClasses.notchedOutline}`]: {
		borderColor: theme.palette.blue['50']
	},
	'&:hover': {
		[`& .${outlinedInputClasses.notchedOutline}`]: {
			borderColor: theme.palette.blue['50']
		}
	},
	'&.Mui-focused': {
		[`& .${outlinedInputClasses.notchedOutline}`]: {
			borderColor: theme.palette.blue['50'],
			borderWidth: 1
		}
	},
	'& fieldset': {
		paddingLeft: 16,
		'& legend': {
			fontSize: 12
		}
	}
}))

const CustomFormControl = styled(FormControl)({
	width: '100%',
	height: 54
})

const SelectFieldContainer = styled(Grid)({
	flexDirection: 'column',
	'& svg': {
		marginRight: 10
	}
})

const CustomMenuItem = styled(MenuItem)({
	fontFamily: fontFamilyMonserrat,
	fontSize: 14
})

export default SelectField